import React, { useEffect, useState, useRef, useCallback } from 'react'
import debounce from 'lodash/debounce'
import Autocomplete from '@mui/lab/Autocomplete'
import { TextField, Typography, Paper } from '@mui/material'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { KeyboardArrowDown } from '@mui/icons-material'
import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import { withStyles } from '~/themes/useStyles'
import Checkbox from '~/components/common/Checkbox'
import { COLORS } from '~/themes/common'

const searchBoxStyle = () => ({
  wrapInputBoxSearch: {
    position: 'relative',
  },
  listBox: {
    fontSize: 14,
    '& .MuiAutocomplete-listbox': {
      '&::-webkit-scrollbar': {
        width: 5,
        height: 5,
      },
      '&::-webkit-scrollbar-track': {
        borderRadius: 10,
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 10,
        backgroundColor: '#b4bfd9',
      },
    },
  },
  searchBoxContainer: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '4px !important',
      paddingBottom: '4px !important',
      paddingRight: '0 !important',
    },
    '& .MuiAutocomplete-root': {
      flex: '1',
      width: '100%',
      '& .MuiInputBase-root .MuiChip-root': {
        margin: '1px 2px',
      },
    },
    '& .MuiChip-label.MuiChip-labelSmall': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: '237px',
      whiteSpace: 'nowrap',
    },
    '& button': {
      zIndex: 1,
    },
    '& svg.MuiSvgIcon-root': {
      marginRight: 7,
    },
  },
  disabledSelect: {
    '& .Mui-disabled': {
      backgroundColor: COLORS.gray.disableGray2,
    },
    '& .MuiInputBase-root .MuiChip-root': {
      backgroundColor: COLORS.gray.grayBorder,
      color: COLORS.gray.darkGray,
      opacity: 1,
    },
  },
  root: {
    height: 19,
  },
  overlay: {
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: 10,
  },
  rootWrapTextfield: {
    width: '100%',
    '& svg.MuiSvgIcon-root': {
      color: COLORS.gray.midGray,
    },
  },
  endDotDotDot: {
    '& input': {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis',
      maxWidth: '80%',
    },
  },
  renderCheckboxOption: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
})

const SearchBox = ({
  classes,
  inputProps,
  apiAction,
  className,
  value,
  onSearchBoxChange, // (e, valuesOnSearchBox) => {}
  handleFilterOption,
  rightComponent,
  isAdd,
  createInventory,
  multiple,
  options: defaultOptions,
  popupIcon,
  disableClearable,
  customShowInput,
  isShowCustomInput,
  autoComplete,
  endDotDotDot,
  disabledAllOption,
  searchIcon,
  checkboxOnSelect,
  getAddLabel,
  allowSearchEmptyKeyword,
  disabled,
  clearOptionsOnOpen,
  dataTestId,
  paperDataTestId,
  inputDataTestId,
  resetOptions,
  resetOptionsCondition,
  ...props
}) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState(defaultOptions || [])
  const [noOptionsTxt, setNoOptionsTxt] = useState('Type to search ')
  const [overlay, setOverlay] = useState(false)
  const [searchResult, setSearchResult] = useState(null)

  useEffect(() => {
    if (defaultOptions) {
      setOptions(defaultOptions)
    }
  }, [defaultOptions])

  useEffect(() => {
    if (!apiAction) {
      setNoOptionsTxt('No options')
    }
  }, [apiAction])

  useEffect(() => {
    if (searchResult) {
      const newOptions = handleFilterOption(searchResult)
      setOptions(newOptions)
    }
  }, [handleFilterOption])

  const getOptionsFromRequest = async (keyword) => {
    if (!keyword) {
      setNoOptionsTxt('Type to search ')
    }
    if (
      (!allowSearchEmptyKeyword ? keyword.trim() : true) &&
      (!value || keyword !== value.label)
    ) {
      setLoading(true)
      if (!checkboxOnSelect) setOptions([])
      setNoOptionsTxt('No options')
      try {
        const res = await apiAction(keyword)
        setLoading(false)
        setSearchResult(res)
        const optionsData = handleFilterOption(res, keyword)
        setOptions(optionsData)
      } catch {
        setNoOptionsTxt('Something went wrong')
        setLoading(false)
        setOptions([])
        setSearchResult(null)
      }
    }
  }

  useEffect(() => {
    if (resetOptions) {
      setOptions([])
    }
  }, [resetOptionsCondition])

  const onInputChangeValue = apiAction
    ? debounce((val) => getOptionsFromRequest(val), 500)
    : (keyword) => {
        const optionsData = handleFilterOption(
          (defaultOptions || []).filter(
            (x) =>
              x.label.toLowerCase().indexOf(keyword.toLowerCase().trim()) !==
              -1,
          ),
        )
        setOptions(optionsData)
      }

  const inputRef = useRef()

  const renderInput = (params) => (
    <div
      className={clsx(classes.wrapInputBoxSearch, {
        [classes.endDotDotDot]: endDotDotDot,
      })}
      role="presentation"
      onClick={() => {
        inputRef.current.focus()
        inputRef.current.click()
      }}
    >
      <TextField
        ref={inputRef}
        variant="outlined"
        autoComplete="new-password"
        classes={{
          root: classes.rootWrapTextfield,
        }}
        {...params}
        {...inputProps}
        InputProps={{
          ...params.InputProps,
          classes: {
            input: classes.root,
            ...params.InputProps.classes,
          },
          inputProps: {
            ...params.inputProps,
            autoComplete: autoComplete ? 'on' : 'off',
            role: 'presentation',
            'data-testid': inputDataTestId,
          },
          endAdornment: searchIcon ? (
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          ) : null,
          ...props.InputProps,
        }}
      />
      {isShowCustomInput && customShowInput}
    </div>
  )

  const RenderNoResult = () => (
    <>
      {!isAdd || !createInventory ? (
        <Typography variant="subtitle2">{noOptionsTxt}</Typography>
      ) : null}
    </>
  )

  const CustomPaper = useCallback(
    (propsPaper) => (
      <Paper
        classes={{ root: classes.listBox }}
        elevation={8}
        {...propsPaper}
        data-testid={paperDataTestId}
      />
    ),
    [paperDataTestId],
  )

  const onChange = useCallback(
    (e, values) => {
      const isShiftKey = e.nativeEvent.shiftKey
      if (isShiftKey && values.length > 1) {
        const start = options.findIndex(
          (item) =>
            item.value === values[0].value && item.label === values[0].label,
        )
        const end = options.findIndex(
          (item) =>
            item.value === values[values.length - 1].value &&
            item.label === values[values.length - 1].label,
        )
        onSearchBoxChange(e, options.slice(start, end + 1))
      } else {
        onSearchBoxChange(e, values)
      }
    },
    [options, onSearchBoxChange],
  )

  return (
    <div className={classes.searchBoxContainer}>
      <Autocomplete
        multiple={multiple}
        renderOption={(selectProps, option) => (
          <li {...selectProps} key={option.value}>
            {option.label || option.title || ''}
          </li>
        )}
        PaperComponent={CustomPaper}
        className={clsx(className, { [classes.disabledSelect]: disabled })}
        getOptionDisabled={(ops) => {
          if (
            disabledAllOption &&
            ops.label === `+ Create New SKU "${ops.sku}"`
          ) {
            return false
          }
          if (disabledAllOption) {
            return true
          }
          return ops.isDisabled
        }}
        disabled={disabled}
        openOnFocus
        loading={loading}
        disableClearable={disableClearable}
        renderInput={renderInput}
        options={options}
        value={value}
        getOptionLabel={(option) => option.label || ''}
        filterOptions={(opt, params) => {
          const { inputValue } = params
          const checkExist = opt.filter(
            (item) =>
              (item[createInventory ? 'sku' : 'name'] || '')
                .toLowerCase()
                .trim() === (inputValue || '').toLowerCase().trim(),
          )
          if (
            isAdd &&
            (inputValue || '').trim() !== '' &&
            (!opt.length || !checkExist.length)
          ) {
            opt.unshift({
              inputValue: params.inputValue,
              label: getAddLabel(params.inputValue),
              isAdd: true,
            })
          }
          if (
            createInventory &&
            (inputValue || '').trim() !== '' &&
            (!opt.length || !checkExist.length)
          ) {
            opt.unshift({
              sku: params.inputValue,
              label: `+ Create New SKU "${params.inputValue}"`,
              value: params.inputValue,
              image: null,
              createInventory: true,
            })
          }
          return opt
        }}
        onInputChange={(_, val) => onInputChangeValue(val)}
        onChange={onChange}
        popupIcon={popupIcon ? <KeyboardArrowDown color="primary" /> : false}
        loadingText={<Typography variant="subtitle2">Loading...</Typography>}
        noOptionsText={<RenderNoResult />}
        size="small"
        onOpen={() => {
          if (clearOptionsOnOpen) {
            setOptions(defaultOptions || [])
          }
          setOverlay(true)
          document.body.style.overflow = 'hidden'
        }}
        onClose={() => {
          setOverlay(false)
          document.body.style.overflow = ''
        }}
        {...(checkboxOnSelect
          ? {
              disableCloseOnSelect: true,
              renderOption: (selectProps, option, { selected }) => (
                <li {...selectProps} key={option.value}>
                  <div className={classes.renderCheckboxOption}>
                    <Checkbox
                      tabIndex={-1}
                      checked={selected}
                      onClick={() => {}}
                    />
                    {option.label}
                  </div>
                </li>
              ),
              isOptionEqualToValue: (option, v) => option.value === v.value,
            }
          : {})}
        data-testid={dataTestId}
        {...props}
      />
      {rightComponent}
      {overlay && <div className={classes.overlay} />}
    </div>
  )
}

SearchBox.defaultProps = {
  inputProps: {},
  onSearchBoxChange: () => {},
  handleFilterOption: (e) => e,
  value: {},
  className: '',
  rightComponent: '',
  isAdd: false,
  multiple: false,
  options: null,
  apiAction: null,
  popupIcon: false,
  disableClearable: false,
  createInventory: false,
  customShowInput: <></>,
  isShowCustomInput: false,
  autoComplete: false,
  endDotDotDot: false,
  disabledAllOption: false,
  searchIcon: false,
  checkboxOnSelect: false,
  getAddLabel: (label) => `Create "${label}"`,
  InputProps: {},
  allowSearchEmptyKeyword: false,
  disabled: false,
  clearOptionsOnOpen: false,
  dataTestId: '',
  paperDataTestId: '',
  inputDataTestId: '',
  resetOptions: false,
  resetOptionsCondition: '',
}

const IValue = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
})

SearchBox.propTypes = {
  classes: PropTypes.shape().isRequired,
  inputProps: PropTypes.shape(),
  apiAction: PropTypes.func,
  onSearchBoxChange: PropTypes.func,
  handleFilterOption: PropTypes.func,
  value: PropTypes.oneOfType([IValue, PropTypes.arrayOf(IValue)]),
  className: PropTypes.string,
  rightComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isAdd: PropTypes.bool,
  multiple: PropTypes.bool,
  popupIcon: PropTypes.bool,
  disableClearable: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape()),
  createInventory: PropTypes.bool,
  isShowCustomInput: PropTypes.bool,
  autoComplete: PropTypes.bool,
  customShowInput: PropTypes.node,
  endDotDotDot: PropTypes.bool,
  disabledAllOption: PropTypes.bool,
  searchIcon: PropTypes.bool,
  checkboxOnSelect: PropTypes.bool,
  getAddLabel: PropTypes.func,
  InputProps: PropTypes.shape(),
  allowSearchEmptyKeyword: PropTypes.bool,
  disabled: PropTypes.bool,
  clearOptionsOnOpen: PropTypes.bool,
  dataTestId: PropTypes.string,
  paperDataTestId: PropTypes.string,
  inputDataTestId: PropTypes.string,
  resetOptions: PropTypes.string,
  resetOptionsCondition: PropTypes.string,
}

export default withStyles(searchBoxStyle)(SearchBox)
